/**
 * "Together you'd have…" — collaborative dex completion, in one line.
 *
 * WHAT SURVIVED THE MOMENTUM CARD. This page is a contest: four cards ranking
 * two trainers against each other. The Momentum card was the counterweight, and
 * most of it restated things the chart and the grind board now say better — a
 * best week, a streak, a pace projection. One figure on it said something
 * nothing else does: what the two of you hold BETWEEN you, and how much of each
 * other's gaps you could fill. That is the only number on Compare where the
 * other trainer is an asset rather than an opponent, and it is the argument for
 * trading stated as a fact rather than as a prompt.
 *
 * So it is no longer a card at the bottom of the page. It sits directly under
 * the head-to-head, where the contest is stated, as its answer.
 *
 * The arithmetic is togetherTotals in lib/collectionStats, where it can be
 * argued with in a test rather than squinted at on a card.
 */
import { useQuery } from "@tanstack/react-query";
import { Users } from "lucide-react";

import { useDexCompletion } from "@/hooks/useDexCompletion";
import { togetherTotals } from "@/lib/collectionStats";

/** The compare payload, as the endpoint returns it. */
interface CompareResponse {
  /**
   * SPECIES-level counts, computed server-side.
   *
   * Absent from an older Worker, in which case this card renders nothing. That
   * is deliberate: the arrays on this payload are keyed by (variant, species,
   * form), so counting them gives the number of dex ROWS a trainer holds, not
   * the number of Pokémon. This card did exactly that and reported "5,403 of
   * 954 (100%)" on device — a figure five times its own denominator, which the
   * clamp then dressed up as a completed dex. No number is better than that
   * one.
   */
  species?: {
    mine: number;
    theirs: number;
    union: number;
    iCanFill: number;
    theyCanFill: number;
  };
}

export function TogetherLine({
  friendId,
  friendName,
}: {
  friendId: number;
  friendName: string;
}) {
  // Same query key the rest of the page uses, so react-query serves this from
  // cache rather than fetching the payload a second time.
  const { data } = useQuery<CompareResponse>({
    queryKey: [`/api/friends/${friendId}/compare`],
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  // The denominator is the released roster, the same one the dex ring divides
  // by — "1,043" means nothing without the size of the thing being completed.
  const { inGoTotal } = useDexCompletion("all");

  if (!data?.species) return null;

  // Species in, species out. togetherTotals is only arithmetic; feeding it row
  // counts was the whole bug.
  const together = togetherTotals(
    data.species.mine,
    data.species.iCanFill,
    data.species.theyCanFill,
  );

  // Nothing to say until at least one dex has been read.
  if (together.union === 0) return null;

  /**
   * TRUNCATED, never rounded, and NOT clamped.
   *
   * The pair's union is counted from their two dexes while the denominator is
   * the released roster, so the two can legitimately disagree — a species the
   * roster has not caught up with puts the union above it. Clamping that to
   * "100%" is how a nonsense figure got to look like a finished dex on device;
   * showing no percentage at all is the honest version of "these two numbers
   * are not comparable right now".
   */
  const pct =
    inGoTotal > 0 && together.union <= inGoTotal
      ? Math.floor((together.union / inGoTotal) * 100)
      : null;

  return (
    <section
      className="v2-neon-card-soft px-4 py-3 mt-3 flex items-start gap-2.5"
      data-testid="section-together"
    >
      <Users
        className="w-3.5 h-3.5 shrink-0 mt-0.5"
        style={{ color: "var(--accent-fill)" }}
        aria-hidden
      />
      <p className="text-[11.5px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
        Together with {friendName} you'd have{" "}
        <Strong>{together.union.toLocaleString()}</Strong>
        {pct != null && (
          <>
            {" of "}
            <Strong>{inGoTotal.toLocaleString()}</Strong>{" "}
            <span style={{ color: "var(--text-tertiary)" }} data-testid="together-pct">
              ({pct}%)
            </span>
          </>
        )}
        . You can fill <Strong>{together.iCanFill}</Strong> of their gaps; they can fill{" "}
        <Strong>{together.theyCanFill}</Strong> of yours.
      </p>
    </section>
  );
}

function Strong({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-bold tabular-nums" style={{ color: "var(--text-ink-bright)" }}>
      {children}
    </span>
  );
}
